import React, { useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import ReusableTable, {
  TableEmptyState,
} from "components/molecules/shared/table/table";
import { useTrainingCtx } from "pages/dashboard/training/useTraining";
import ModalDaftarTraining from "pages/dashboard/training/components/modal-daftar-training";
import ModalUnggahLampiran from "pages/dashboard/training/components/modal-unggah-lampiran";
import ModalDetailParticipant from "pages/dashboard/training/components/modal-detail-participant/modal-detail-participant";
import ModalKonfirmasiNegative from "pages/dashboard/training/components/modal-konfirmasi-negative";
import { useRegisterParticipant, useDeleteCalonMa } from "api/training/mutation";

const widthContainer = 1139;

const ColumnSizes = {
  no: 31,
  noRegistrasi: 100,
  id: 88,
  nama: 316,
  sisaAktif: 316,
  aksi: 135,
};

const _getSize = (size) => {
  return `${(size / widthContainer) * 100}%`;
};

const ContentJMA = () => {
  const {
    listJma,
    isLoading,
    listAllTrainingAndBatches,
    refetchAll,
    isPaidBatchOpen,
  } = useTrainingCtx();

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [isOpenModalRegister, setIsOpenModalRegister] = useState(false);
  const [isModalUnduhLampiran, setIsModalUnduhLampiran] = useState(false);
  const [isUploadLampiran, setUploadLampiran] = useState(false);
  const [isOpenModalDetail, setIsOpenModalDetail] = useState(false);
  const [selectedUserIdForDetail, setSelectedUserIdForDetail] = useState(null);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);

  const { mutate: mutateRegisterParticipant } = useRegisterParticipant();
  const { mutate: mutateDeleteCalonMa } = useDeleteCalonMa();

  const jmaData = useMemo(() => {
    if (!listJma?.pages) return [];
    return listJma.pages.flatMap((page) => page?.data?.data || []);
  }, [listJma]);

  const handleOpenModalRegister = useCallback((userId) => {
    setSelectedUserId(userId);
    setIsOpenModalRegister(true);
  }, []);

  const handleCloseModalRegister = () => {
    setIsOpenModalRegister(false);
    setSelectedUserId(null);
  };

  const handleOpenModalUnduhLampiran = () => setIsModalUnduhLampiran(true);
  const handleCloseModalUnduhLampiran = () => setIsModalUnduhLampiran(false);

  const handleOpenModalDetail = useCallback((userId) => {
    setSelectedUserIdForDetail(userId);
    setIsOpenModalDetail(true);
  }, []);

  const handleCloseModalDetail = () => {
    setIsOpenModalDetail(false);
    // Don't reset selectedUserIdForDetail here, it's needed for delete modal
  };

  const handleOpenModalDelete = () => {
    setIsOpenModalDelete(true);
  };

  const handleCloseModalDelete = () => {
    setIsOpenModalDelete(false);
    setSelectedUserIdForDetail(null);
  };

  const handleDeleteJma = () => {
    mutateDeleteCalonMa(selectedUserIdForDetail, {
      onSuccess: () => {
        handleCloseModalDelete();
        handleCloseModalDetail();
        refetchAll();
        toast.success("Berhasil menghapus Junior Marketing Associate");
      },
      onError: (error) => {
        toast.error(error?.message);
      }
    });
  };

  const handlePreUnduhLaporan = (batchId) => {
    setBatchId(batchId);
    handleOpenModalUnduhLampiran();
  };

  const handleRegisterParticipant = (
    batchId,
    otherFile,
    agreementFile,
    integrityFile,
  ) => {
    const formData = new FormData();
    formData.append("training_batch_participant[user_id]", selectedUserId);
    agreementFile && formData.append("agreement_document", agreementFile);
    integrityFile && formData.append("integrity_document", integrityFile);
    otherFile && formData.append("other_document", otherFile);

    const payload = {
      id: batchId,
      data: formData,
    };

    mutateRegisterParticipant(payload, {
      onSuccess: () => {
        handleCloseModalRegister();
        handleCloseModalUnduhLampiran();
        refetchAll();
        toast.success("Berhasil mendaftarkan participant");
      },
      onError: (error) => {
        toast.error(error?.message);
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "No.",
        accessor: "no",
        width: _getSize(ColumnSizes.no),
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "No. Registrasi",
        accessor: "registration_number",
        width: _getSize(ColumnSizes.noRegistrasi),
      },
      {
        Header: "ID",
        accessor: "id",
        width: _getSize(ColumnSizes.id),
      },
      {
        Header: "Nama",
        accessor: "full_name",
        width: _getSize(ColumnSizes.nama),
        Cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="flex items-center gap-4">
              <div>
                <img
                  src={user?.user_image || "https://d2b29hgfximz6c.cloudfront.net/assets/default/image-9191024a98ba23d13cd7ab02379ee5faca5d3830aa7f3f49eaca7cedb65999d1.png"}
                  alt={user?.full_name || "Profile"}
                  className="w-[40px] aspect-square rounded-full object-cover"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter text-[#252526]">
                  {user.full_name}
                </span>
                {user.role_name && (
                  <span className="text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-[#808285]">
                    {user.role_name}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Sisa Aktif",
        accessor: "registration_number_expiry",
        width: _getSize(ColumnSizes.sisaAktif),
        Cell: ({ value }) => {
          if (!value || value === "Tidak Aktif-Masa aktif selesai") {
            return (
              <span className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter text-[#808285]">
                {value || "-"}
              </span>
            );
          }
          const daysRemaining = dayjs(value).diff(dayjs(), "day");
          const formattedDate = dayjs(value).format("DD MMMM YYYY");
          return (
            <div className="flex flex-col">
              <span className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter text-[#252526]">
                {daysRemaining > 0 ? `${daysRemaining} hari lagi` : "Expired"}
              </span>
              <span className="text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-[#808285]">
                {formattedDate}
              </span>
            </div>
          );
        },
      },
      {
        Header: "Aksi",
        accessor: "actions",
        width: _getSize(ColumnSizes.aksi),
        Cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenModalDetail(user.id)}
                className="px-3 py-1.5 text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-[#252526] border border-solid border-[#D8D8D9] rounded-[4px] hover:bg-[#F5F5F5]"
              >
                Detail
              </button>
              {isPaidBatchOpen && (
                <button
                  onClick={() => handleOpenModalRegister(user.id)}
                  className="px-3 py-1.5 text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-white bg-[#252526] rounded-[4px] hover:bg-[#3a3a3a]"
                >
                  Daftarkan Training
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [isPaidBatchOpen, handleOpenModalRegister, handleOpenModalDetail],
  );

  const customHeaderRenderer = ({ headerGroups }) => {
    return headerGroups.map((headerGroup) => (
      <div
        {...headerGroup.getHeaderGroupProps()}
        className="flex items-center px-4 py-2 gap-[24px] bg-[#FAFAFA] border-y-[0.5px] border-solid border-[#DEDFDE] rounded-t-[4px]"
      >
        {headerGroup.headers.map((column) => (
          <div
            {...column.getHeaderProps()}
            style={{
              width: column.width || "auto",
              minWidth: column.minWidth || "auto",
              maxWidth: column.maxWidth || "none",
            }}
          >
            <span className="text-[#686A68] text-[13px] leading-[21px] tracking-[-0.4px] font-inter-semibold">
              {column.render("Header")}
            </span>
          </div>
        ))}
      </div>
    ));
  };

  return (
    <div className="w-full">
      <ReusableTable
        columns={columns}
        data={jmaData}
        isLoading={isLoading}
        emptyState={
          <TableEmptyState
            title="Belum ada data Junior Marketing Associate"
            subtitle="Data Junior Marketing Associate akan ditampilkan di sini."
          />
        }
        minWidth="1200px"
        renderHeader={customHeaderRenderer}
        rowClassName="flex items-center px-4 py-[10px] gap-[24px] border-b-[0.5px] border-solid border-[#D8D8D9] bg-white"
      />

      <ModalDaftarTraining
        isOpen={isOpenModalRegister}
        onRequestClose={handleCloseModalRegister}
        onSubmit={handleRegisterParticipant}
        list={listAllTrainingAndBatches?.data?.data ?? []}
        onUnduhLaporan={handlePreUnduhLaporan}
        isUploadLampiran={isUploadLampiran}
        setUploadLampiran={setUploadLampiran}
        isPaidOnly
        isPassedParticipant
        isJMA
      />

      <ModalUnggahLampiran
        isOpen={isModalUnduhLampiran}
        onRequestClose={handleCloseModalUnduhLampiran}
        onSubmit={handleRegisterParticipant}
        batchId={batchId}
        isUploadLampiran={isUploadLampiran}
      />

      <ModalDetailParticipant
        isOpen={isOpenModalDetail}
        onRequestClose={handleCloseModalDetail}
        isJma
        onSuccess={() => {
          handleCloseModalDetail();
          refetchAll();
        }}
        onDelete={handleOpenModalDelete}
        userId={selectedUserIdForDetail}
      />

      <ModalKonfirmasiNegative
        isOpen={isOpenModalDelete}
        onRequestClose={handleCloseModalDelete}
        title="Yakin hapus Junior Marketing Associate ini?"
        info="Data Junior Marketing Associate akan dihapus dari sistem."
        onSubmit={handleDeleteJma}
      />
    </div>
  );
};

export default ContentJMA;
